import React, { useEffect, useState, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { InputTextarea } from 'primereact/inputtextarea';
import { RadioButton } from 'primereact/radiobutton';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import DealerDropdown from '../../../../../components/common/DealerDropdown';
import DealerAddressDropdown from '../../../../../components/common/DealerAddressDropdown';
import { useLanguage } from '../../../../../contexts/LanguageContext';

// Default form state matching API structure
const defaultFormState = {
    id: null,
    dealerId: null,
    dealerName: '',
    dealerAddressId: null,
    dealerAddressName: '',
    contractStartDate: null,
    contractEndDate: null,
    orderTypeId: null,
    orderTypeName: '',
    usageType: null,
    description: '',
    attribute1: null,
    attribute2: null,
    attribute3: null,
    isActive: 1
};

const CustomerOrderTypeForm = ({ visible, onHide, onSubmit, sapOrderTypes, submitted, editMode = false, selectedItem = null }) => {
    const { th } = useLanguage();
    // Local form state management
    const [formData, setFormData] = useState(defaultFormState);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [selectedAddress, setSelectedAddress] = useState(null);
    const [localSubmitted, setLocalSubmitted] = useState(false);
    const toast = useRef(null);

    // Reset form when dialog closes
    useEffect(() => {
        if (!visible) {
            setSelectedCustomer(null);
            setSelectedAddress(null);
            setFormData(defaultFormState);
            setLocalSubmitted(false);
        }
    }, [visible]);

    // Initialize form data when editing or reset for new item
    useEffect(() => {
        if (visible) {
            if (editMode && selectedItem) {
                // Map API data to form state for editing
                setFormData({
                    id: selectedItem.id || null,
                    dealerId: selectedItem.dealerId || null,
                    dealerName: selectedItem.dealerName || '',
                    dealerAddressId: selectedItem.dealerAddressId || null,
                    dealerAddressName: selectedItem.dealerAddressName || '',
                    contractStartDate: selectedItem.contractStartDate ? new Date(selectedItem.contractStartDate) : null,
                    contractEndDate: selectedItem.contractEndDate ? new Date(selectedItem.contractEndDate) : null,
                    orderTypeId: selectedItem.orderTypeId || null,
                    orderTypeName: selectedItem.orderTypeName || '',
                    usageType: selectedItem.usageType || null,
                    description: selectedItem.description || '',
                    attribute1: selectedItem.attribute1 || null,
                    attribute2: selectedItem.attribute2 || null,
                    attribute3: selectedItem.attribute3 || null,
                    isActive: selectedItem.isActive || 1
                });

                // Set selected customer and address for dropdowns
                if (selectedItem.dealerId) {
                    setSelectedCustomer({
                        id: selectedItem.dealerId,
                        dealerId: selectedItem.dealerId,
                        dealerName: selectedItem.dealerName
                    });
                }

                if (selectedItem.dealerAddressId) {
                    // Add a small delay to ensure address dropdown has loaded
                    setTimeout(() => {
                        setSelectedAddress({
                            id: selectedItem.dealerAddressId,
                            address: selectedItem.dealerAddressName,
                            displayValue: `${selectedItem.dealerAddressId} - ${selectedItem.dealerAddressName}`
                        });
                    }, 500);
                }
            } else if (!editMode) {
                // Reset to default state for new item - ensure immediate reset
                setFormData({
                    id: null,
                    dealerId: null,
                    dealerName: '',
                    dealerAddressId: null,
                    dealerAddressName: '',
                    contractStartDate: null,
                    contractEndDate: null,
                    orderTypeId: null,
                    orderTypeName: '',
                    usageType: null,
                    description: '',
                    attribute1: null,
                    attribute2: null,
                    attribute3: null,
                    isActive: 1
                });
                setSelectedCustomer(null);
                setSelectedAddress(null);
            }
        }
    }, [visible, selectedItem, editMode]);

    // Handle address loading when dealer is set in edit mode
    useEffect(() => {
        if (editMode && selectedCustomer && selectedItem?.dealerAddressId && !selectedAddress) {
            // Wait a bit more for address dropdown to load
            const timer = setTimeout(() => {
                setSelectedAddress({
                    id: selectedItem.dealerAddressId,
                    address: selectedItem.dealerAddressName,
                    displayValue: `${selectedItem.dealerAddressId} - ${selectedItem.dealerAddressName}`
                });
            }, 1000);

            return () => clearTimeout(timer);
        }
    }, [selectedCustomer, editMode, selectedItem, selectedAddress]);

    const handleDealerChange = (dealer) => {
        if (dealer && typeof dealer.id !== 'undefined') {
            const normalized = {
                ...dealer,
                id: Number(dealer.id),
                dealerId: Number(dealer.id)
            };
            setSelectedCustomer(normalized);

            // Update form data
            setFormData((prev) => {
                const updated = {
                    ...prev,
                    dealerId: Number(dealer.id),
                    dealerName: dealer.dealerName || dealer.name || ''
                };
                return updated;
            });
        } else {
            setSelectedCustomer(null);
            setFormData((prev) => ({
                ...prev,
                dealerId: null,
                dealerName: ''
            }));
        }

        // Reset address when dealer changes
        setSelectedAddress(null);
        setFormData((prev) => ({
            ...prev,
            dealerAddressId: null,
            dealerAddressName: ''
        }));
    };

    const handleAddressChange = (address) => {
        setSelectedAddress(address);

        // Update form data
        setFormData((prev) => {
            const updated = {
                ...prev,
                dealerAddressId: address?.id || null,
                dealerAddressName: address?.address || address?.displayValue || ''
            };
            return updated;
        });
    };

    const handleOrderTypeChange = (orderType) => {
        setFormData((prev) => {
            const updated = {
                ...prev,
                orderTypeId: orderType?.orderTypeId || null,
                orderTypeName: orderType?.orderTypeErpName || ''
            };
            return updated;
        });
    };

    const handleFieldChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = () => {
        setLocalSubmitted(true);
        // Validasyon kontrolleri ve uyarı mesajları
        const validationErrors = [];
        if (!formData.dealerId) {
            validationErrors.push(th('customerOrderType.dealerRequired'));
        }
        if (!formData.dealerAddressId) {
            validationErrors.push(th('customerOrderType.addressRequired'));
        }
        if (!formData.orderTypeId) {
            validationErrors.push(th('customerOrderType.orderTypeRequired'));
        }
        if (formData.usageType === null) {
            validationErrors.push(th('customerOrderType.usageTypeRequired'));
        }
        if (!formData.contractStartDate) {
            validationErrors.push(th('customerOrderType.startDateRequired'));
        }

        // Eğer validasyon hatası varsa uyarı mesajlarını göster
        if (validationErrors.length > 0) {
            validationErrors.forEach((error, index) => {
                setTimeout(() => {
                    toast.current?.show({
                        severity: 'error',
                        summary: th('customerOrderType.missingField'),
                        detail: error,
                        life: 4000
                    });
                }, index * 200); // Her mesajı biraz gecikmeyle göster
            });
            return;
        }

        const submitData = {
            ...formData,
            contractStartDate: formData.contractStartDate.toISOString(),
            contractEndDate: formData.contractEndDate ? formData.contractEndDate.toISOString() : null,
            isActive: formData.isActive ? 1 : 0
        };

        if (onSubmit) {
            onSubmit(submitData);
        } else {
            console.log('❌ onSubmit function is not provided');
        }
    };

    const handleClose = () => {
        onHide();
    };

    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button label={th('common.cancel')} icon="pi pi-times" outlined onClick={handleClose} />
            <Button label={th('common.save')} icon="pi pi-check" onClick={handleSubmit} />
        </div>
    );

    return (
        <>
            <Toast ref={toast} />
            <Dialog header={editMode ? th('customerOrderType.editTitle') : th('customerOrderType.addTitle')} visible={visible} style={{ width: '45vw' }} modal footer={footer} onHide={handleClose} className="p-fluid">
                <div className="formgrid grid">
                    <div className="field col-12 md:col-6">
                        <label htmlFor="dealer" className="font-bold">
                            {th('customerOrderType.dealer')}
                        </label>
                        <DealerDropdown
                            value={selectedCustomer?.id?.toString() || selectedCustomer?.dealerId?.toString()}
                            onChange={handleDealerChange}
                            placeholder={th('customerOrderType.dealerPlaceholder')}
                            className={localSubmitted && !formData.dealerId ? 'p-invalid' : ''}
                        />
                        {localSubmitted && !formData.dealerId && <small className="p-error">{th('customerOrderType.dealerRequired')}</small>}
                    </div>
                    <div className="field col-12 md:col-6">
                        <label htmlFor="address" className="font-bold">
                            {th('customerOrderType.address')}
                        </label>
                        <DealerAddressDropdown value={selectedAddress} onChange={handleAddressChange} dealerId={formData.dealerId} className={`w-full ${localSubmitted && !formData.dealerAddressId ? 'p-invalid' : ''}`} disabled={!formData.dealerId} />
                        {localSubmitted && !formData.dealerAddressId && <small className="p-error">{th('customerOrderType.addressRequired')}</small>}
                    </div>

                    <div className="field col-12 md:col-6">
                        <label htmlFor="orderType" className="font-bold">
                            {th('customerOrderType.orderType')}
                        </label>
                        <Dropdown
                            id="orderType"
                            value={formData.orderTypeId ? Number(formData.orderTypeId) : null}
                            options={sapOrderTypes}
                            onChange={(e) => {
                                const selected = sapOrderTypes.find((x) => x.orderTypeId === e.value);
                                setFormData((prev) => ({
                                    ...prev,
                                    orderTypeId: e.value,
                                    orderTypeName: selected?.orderTypeErpName || ''
                                }));
                            }}
                            optionLabel="orderTypeErpName"
                            optionValue="orderTypeId"
                            placeholder={th('customerOrderType.orderTypePlaceholder')}
                            filter
                            showClear
                            className={localSubmitted && !formData.orderTypeId ? 'p-invalid' : ''}
                        />
                        {localSubmitted && !formData.orderTypeId && <small className="p-error">{th('customerOrderType.orderTypeRequired')}</small>}
                    </div>

                    <div className="field col-12 md:col-6">
                        <label className="font-bold">{th('customerOrderType.usageType')}</label>
                        <div className="flex flex-wrap gap-3 mt-2">
                            <div className="flex align-items-center">
                                <RadioButton inputId="usageService" name="usageType" value={1} onChange={(e) => handleFieldChange('usageType', e.value)} checked={formData.usageType === 1} />
                                <label htmlFor="usageService" className="ml-2">
                                    {th('customerOrderType.service')}
                                </label>
                            </div>
                            <div className="flex align-items-center">
                                <RadioButton inputId="usageSales" name="usageType" value={2} onChange={(e) => handleFieldChange('usageType', e.value)} checked={formData.usageType === 2} />
                                <label htmlFor="usageSales" className="ml-2">
                                    {th('customerOrderType.sales')}
                                </label>
                            </div>
                        </div>
                        {localSubmitted && formData.usageType === null && <small className="p-error">{th('customerOrderType.usageTypeRequired')}</small>}
                    </div>

                    <div className="field col-12 md:col-6">
                        <label htmlFor="startDate" className="font-bold">
                            {th('customerOrderType.startDate')}
                        </label>
                        <Calendar
                            id="startDate"
                            value={formData.contractStartDate}
                            onChange={(e) => handleFieldChange('contractStartDate', e.value)}
                            dateFormat="dd/mm/yy"
                            showIcon
                            placeholder={th('customerOrderType.startDatePlaceholder')}
                            className={localSubmitted && !formData.contractStartDate ? 'p-invalid' : ''}
                        />
                        {localSubmitted && !formData.contractStartDate && <small className="p-error">{th('customerOrderType.startDateRequired')}</small>}
                    </div>

                    <div className="field col-12 md:col-6">
                        <label htmlFor="endDate" className="font-bold">
                            {th('customerOrderType.endDate')}
                        </label>
                        <Calendar
                            id="endDate"
                            value={formData.contractEndDate}
                            onChange={(e) => handleFieldChange('contractEndDate', e.value)}
                            dateFormat="dd/mm/yy"
                            showIcon
                            placeholder={th('customerOrderType.endDatePlaceholder')}
                            minDate={formData.contractStartDate}
                        />
                    </div>

                    <div className="field col-12">
                        <label htmlFor="description" className="font-bold">
                            {th('customerOrderType.description')}
                        </label>
                        <InputTextarea id="description" value={formData.description} onChange={(e) => handleFieldChange('description', e.target.value)} rows={3} placeholder={th('customerOrderType.descriptionPlaceholder')} />
                    </div>

                    <div className="field-checkbox col-12">
                        <Checkbox inputId="isActive" checked={formData.isActive === 1} onChange={(e) => handleFieldChange('isActive', e.checked ? 1 : 0)} />
                        <label htmlFor="isActive" className="ml-2">
                            {th('customerOrderType.activeStatus')}
                        </label>
                    </div>
                </div>
            </Dialog>
        </>
    );
};

export default CustomerOrderTypeForm;
